import React, { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@clerk/clerk-react"
import { useHistory } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { abandonDemoEntryAnalysis, pollDemoJob, retryDemoTes } from "@/services/demoEntryAnalysisApi"
import type { DemoJobStatus } from "@/types/demoEntryAnalysis"
import { trackDemoEntryAnalysisEvent } from "@/utils/demoEntryAnalysisAnalytics"
import {
  allowDemoClaimRetryAfterCookieFix,
  claimDemoJobOnce,
  shouldSkipDemoClaim,
} from "@/utils/demoEntryAnalysisClaim"
import {
  DEMO_SESSION_EXPIRED_COPY,
  DEMO_TES_DAILY_LIMIT_COPY,
  demoErrorUserMessage,
  isDemoPollUnauthorized,
  isDemoRateLimited,
  isDemoTesDailyUserLimit,
} from "@/utils/demoEntryAnalysisErrors"
import {
  canAttemptHandoffRedeem,
  redeemDemoHandoffOnce,
} from "@/utils/demoEntryAnalysisHandoff"
import {
  DEMO_ENTRY_ANALYSIS_BASE_PATH,
  clearPendingAuthRedirect,
  demoSigninRedirectUrl,
  demoSignupRedirectUrl,
  readDemoSession,
  restartDemoEntryAnalysis,
  writeDemoSession,
} from "@/utils/demoEntryAnalysisSession"
import {
  AUTH_FONT,
  AUTH_MUTED_TEXT_CLASS,
  AUTH_TROPICAL,
  DemoCenteredMessage,
  DemoEntryAnalysisShell,
  DemoLoadingBlock,
  DemoLoopingEllipsis,
} from "./DemoEntryAnalysisShell"

const DEMO_REPORT_PREVIEW_SRC =
  // Same master as `public/Background Images/Statistics Overview Page Image.png`
  // (CRA cannot serve paths with spaces).
  "/assets/demo/statistics-overview-preview.png"

/** Staged status copy — phase 1 maps to 0–50%, phase 2 continues 50–100%. */
const GENERATING_STEPS = [
  { id: "collect", label: "Collecting trade data" },
  { id: "analyse", label: "Analysing your trades" },
  { id: "leaks", label: "Finding entry leaks" },
  { id: "report", label: "Building your report" },
] as const

const GENERATING_STEP_MS = 2400
/** Pause at 50% before crossfading to the signup gate. */
const GENERATING_COMPLETE_HOLD_MS = 1100
const UNSIGNED_GATE_CROSSFADE_MS = 700
const GENERATING_STEP_COUNT = GENERATING_STEPS.length
const PRE_SIGNUP_PROGRESS_MAX = 50
const POST_SIGNUP_PROGRESS_MIN = 50
/** Post-signup step targets (must stay above POST_SIGNUP_PROGRESS_MIN). */
const POST_SIGNUP_STEP_TARGETS = [62, 74, 86, 94] as const
/** Creep toward this on the last step while the job finishes (then 100% when ready). */
const POST_SIGNUP_HOLD_MAX = 99

type GeneratingProgressPhase = "pre_signup" | "post_signup"

function generatingStepTargetPct(
  stepIndex: number,
  phase: GeneratingProgressPhase,
  holdBeforeComplete: boolean
): number {
  const clamped = Math.min(Math.max(stepIndex, 0), GENERATING_STEP_COUNT - 1)
  const fraction = (clamped + 1) / GENERATING_STEP_COUNT
  if (phase === "pre_signup") {
    return fraction * PRE_SIGNUP_PROGRESS_MAX
  }
  const stepTarget =
    POST_SIGNUP_STEP_TARGETS[clamped] ?? POST_SIGNUP_STEP_TARGETS[POST_SIGNUP_STEP_TARGETS.length - 1]
  const atLastStep = clamped >= GENERATING_STEP_COUNT - 1
  if (atLastStep && !holdBeforeComplete) {
    return 100
  }
  return stepTarget
}


function DemoGeneratingProgress(props: {
  activeStepIndex: number
  progressPhase: GeneratingProgressPhase
  holdBeforeComplete?: boolean
  footerNote?: string | null
  /** Last pre-signup step — softer copy before the signup gate fades in. */
  gatePending?: boolean
}): React.ReactElement {
  const {
    activeStepIndex,
    progressPhase,
    holdBeforeComplete = false,
    footerNote,
    gatePending = false,
  } = props
  const progressMin =
    progressPhase === "pre_signup" ? 0 : POST_SIGNUP_PROGRESS_MIN
  const progressSpan =
    progressPhase === "pre_signup"
      ? PRE_SIGNUP_PROGRESS_MAX
      : 100 - POST_SIGNUP_PROGRESS_MIN
  const clamped = Math.min(
    Math.max(activeStepIndex, 0),
    GENERATING_STEP_COUNT - 1
  )
  const active = GENERATING_STEPS[clamped]
  const stepLabel = gatePending ? "Almost ready" : active.label
  const atLastPostSignupStep =
    progressPhase === "post_signup" && clamped >= GENERATING_STEP_COUNT - 1
  const [holdCreepBonus, setHoldCreepBonus] = useState(0)
  const baseTargetPct = generatingStepTargetPct(
    clamped,
    progressPhase,
    progressPhase === "post_signup" && holdBeforeComplete
  )
  const targetPct =
    atLastPostSignupStep && holdBeforeComplete
      ? Math.min(POST_SIGNUP_HOLD_MAX, baseTargetPct + holdCreepBonus)
      : baseTargetPct
  const [progress, setProgress] = useState(() =>
    progressPhase === "post_signup" ? POST_SIGNUP_PROGRESS_MIN : 4
  )

  useEffect(() => {
    if (!atLastPostSignupStep || !holdBeforeComplete) {
      setHoldCreepBonus(0)
      return
    }
    const id = window.setInterval(() => {
      setHoldCreepBonus((bonus) => Math.min(POST_SIGNUP_HOLD_MAX - baseTargetPct, bonus + 0.12))
    }, 350)
    return () => window.clearInterval(id)
  }, [atLastPostSignupStep, baseTargetPct, holdBeforeComplete])

  useEffect(() => {
    let raf = 0
    let last = performance.now()

    const floor =
      progressPhase === "pre_signup"
        ? progressMin + (clamped / GENERATING_STEP_COUNT) * progressSpan
        : POST_SIGNUP_STEP_TARGETS[clamped] ?? POST_SIGNUP_PROGRESS_MIN
    setProgress((prev) => Math.max(prev, Math.min(floor, targetPct)))

    const tick = (now: number) => {
      const dt = Math.min(now - last, 64)
      last = now
      setProgress((prev) => {
        if (prev >= targetPct) return targetPct
        const delta = targetPct - prev
        const step = Math.max(0.08, delta * Math.min(1, dt / 900))
        return Math.min(targetPct, prev + step)
      })
      raf = window.requestAnimationFrame(tick)
    }

    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [clamped, progressMin, progressSpan, targetPct])

  const pct = Math.min(100, Math.max(0, progress))
  const displayPct = Math.round(pct)
  const titleText =
    progressPhase === "post_signup" ? "Generating your report" : stepLabel

  return (
    <div
      className="flex min-h-[16rem] w-full flex-1 flex-col items-center justify-center gap-6 px-5 text-center sm:min-h-[18rem] sm:gap-7 sm:px-4"
      data-demo-generating-progress=""
    >
      <h1
        data-demo-wizard-title=""
        className={cn(AUTH_FONT, "w-full text-center font-normal")}
        aria-live="polite"
      >
        <span className="inline-flex items-baseline justify-center">
          <span>{titleText}</span>
          <DemoLoopingEllipsis />
        </span>
      </h1>

      <div
        className="flex w-full max-w-[17.5rem] flex-col items-center gap-4 px-1 sm:max-w-md sm:gap-5 sm:px-0"
        data-demo-generating-progress-bar=""
      >
        <div className="flex w-full flex-col gap-2">
          <div className="flex items-baseline justify-between gap-3">
            <span className={cn(AUTH_FONT, AUTH_MUTED_TEXT_CLASS, "text-xs font-normal")}>
              Progress
            </span>
            <span
              className={cn(AUTH_FONT, AUTH_TROPICAL, "tabular-nums text-sm font-normal")}
              aria-hidden
            >
              {displayPct}%
            </span>
          </div>
          <div
            className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200/80"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={displayPct}
            aria-valuetext={`${titleText}, ${displayPct}%`}
            aria-label="Report generation progress"
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${pct}%`,
                backgroundImage: "var(--gradient-primary)",
              }}
            />
          </div>
        </div>
      </div>

      {footerNote ? (
        <p className={cn(AUTH_FONT, AUTH_MUTED_TEXT_CLASS, "max-w-md px-2 text-sm sm:px-0")}>
          {footerNote}
        </p>
      ) : null}
    </div>
  )
}

function DemoUnsignedSignupGate(props: {
  unsignedPhase: "loading" | "signup"
  preSignupStepIndex: number
  footerNote?: string | null
}): React.ReactElement {
  const showSignup = props.unsignedPhase === "signup"
  const gatePending =
    !showSignup && props.preSignupStepIndex >= GENERATING_STEP_COUNT - 1

  return (
    <div
      data-demo-unsigned-gate=""
      className="relative -mx-6 -my-6 flex min-h-[min(68vh,34rem)] flex-1 flex-col md:-mx-8 md:-my-8"
    >
      <div
        className={cn(
          "pointer-events-none absolute inset-0 overflow-hidden transition-opacity ease-out motion-reduce:transition-none",
          showSignup ? "opacity-100" : "opacity-0"
        )}
        style={{ transitionDuration: `${UNSIGNED_GATE_CROSSFADE_MS}ms` }}
        aria-hidden
      >
        <img
          src={DEMO_REPORT_PREVIEW_SRC}
          alt=""
          className="h-full w-full scale-[1.08] object-cover object-top blur-[4px] sm:blur-[6px]"
          decoding="async"
        />
        <div className="absolute inset-0 bg-white/25" />
      </div>

      <div
        className={cn(
          "relative z-10 flex flex-1 flex-col ease-out motion-reduce:transition-none motion-reduce:transform-none motion-reduce:blur-none",
          "transition-all motion-safe:duration-700",
          showSignup
            ? "pointer-events-none absolute inset-0 opacity-0 motion-safe:scale-[0.98] motion-safe:blur-[1px]"
            : "opacity-100 motion-safe:scale-100"
        )}
        aria-hidden={showSignup}
      >
        <DemoGeneratingProgress
          activeStepIndex={props.preSignupStepIndex}
          progressPhase="pre_signup"
          footerNote={props.footerNote}
          gatePending={gatePending}
        />
      </div>

      <div
        className={cn(
          "relative z-20 flex flex-1 flex-col items-center justify-center px-4 py-10 ease-out motion-reduce:transition-none motion-reduce:transform-none sm:px-8 sm:py-14",
          "transition-all motion-safe:duration-700 motion-safe:delay-150",
          showSignup
            ? "opacity-100 motion-safe:translate-y-0"
            : "pointer-events-none absolute inset-0 opacity-0 motion-safe:translate-y-2"
        )}
        aria-hidden={!showSignup}
      >
        <div
          className={cn(
            "w-full max-w-xl rounded-[6px] border border-border/60 bg-white px-8 py-10 text-center shadow-[0_16px_48px_rgba(15,23,42,0.14)] sm:px-12 sm:py-12",
            "transition-all ease-out motion-reduce:transition-none motion-safe:duration-700 motion-safe:delay-200",
            showSignup
              ? "opacity-100 motion-safe:translate-y-0 motion-safe:scale-100"
              : "opacity-0 motion-safe:translate-y-3 motion-safe:scale-[0.98]"
          )}
        >
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            <h1 data-demo-wizard-title="" className={cn(AUTH_FONT, "font-normal")}>
              Sign up to see your results
            </h1>
            <p
              className={cn(
                AUTH_FONT,
                "max-w-md text-pretty text-sm font-normal leading-relaxed text-gray-500 sm:text-base"
              )}
            >
              Your report is ready for review. Create a free account to unlock it.
            </p>
          </div>
          <div className="mt-8 flex w-full flex-col gap-4">
            <Button
              type="button"
              variant="gradient"
              className="w-full"
              onClick={() => window.location.assign(demoSignupRedirectUrl())}
            >
              Sign up free
            </Button>
            <p
              className={cn(
                AUTH_FONT,
                AUTH_MUTED_TEXT_CLASS,
                "text-center text-sm font-normal"
              )}
            >
              Already have an account?{" "}
              <button
                type="button"
                data-demo-sign-in-link=""
                className={cn(
                  AUTH_FONT,
                  AUTH_TROPICAL,
                  "inline font-medium hover:opacity-90 !text-[color:var(--brand-tropical-surf)]"
                )}
                onClick={() => window.location.assign(demoSigninRedirectUrl())}
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function DemoGeneratingPage(): React.ReactElement {
  const history = useHistory()
  const { isLoaded, isSignedIn } = useAuth()

  const session = readDemoSession()
  const jobId = session?.jobId

  const [status, setStatus] = useState<DemoJobStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [claimBlocked, setClaimBlocked] = useState(false)
  const [claimBlockKind, setClaimBlockKind] = useState<"rate_limit" | "tes_daily" | "other">("other")
  const [preSignupStepIndex, setPreSignupStepIndex] = useState(0)
  const [postSignupStepIndex, setPostSignupStepIndex] = useState(0)
  const [unsignedPhase, setUnsignedPhase] = useState<"loading" | "signup">("loading")
  const [retrying, setRetrying] = useState(false)

  const claimedRef = useRef(Boolean(session?.claimed))
  const redeemAttemptedRef = useRef(false)
  const pollStoppedRef = useRef(false)
  const claimBlockedRef = useRef(false)
  const claimKickoffRef = useRef(false)
  const unsignedPhaseRef = useRef(unsignedPhase)
  unsignedPhaseRef.current = unsignedPhase

  useEffect(() => {
    if (jobId || sessionExpired) return
    const latest = readDemoSession()
    // Back from report clears jobId but keeps the cohort — land on wizard, not import.
    if (latest?.step === "wizard" && latest.cohortId) {
      history.replace(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/wizard`)
      return
    }
    history.replace(DEMO_ENTRY_ANALYSIS_BASE_PATH)
  }, [history, jobId, sessionExpired])

  /**
   * Browser back from generating/signup → instrument/import abandons the prep lock
   * so Generate my report can start a fresh job.
   */
  useEffect(() => {
    return history.listen((location, action) => {
      if (action !== "POP") return
      const path = location.pathname || ""
      const backToWizard = path.startsWith(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/wizard`)
      const backToImport =
        path === DEMO_ENTRY_ANALYSIS_BASE_PATH ||
        path === `${DEMO_ENTRY_ANALYSIS_BASE_PATH}/`
      if (!backToWizard && !backToImport) return

      const prior = readDemoSession()
      const priorJobId = prior?.jobId
      void abandonDemoEntryAnalysis(priorJobId).catch(() => {})
      writeDemoSession({
        step: backToWizard ? "wizard" : "import",
        jobId: undefined,
        handoffCode: undefined,
        pollToken: undefined,
        claimed: false,
        claimAttempted: false,
        readyPayload: null,
      })
      trackDemoEntryAnalysisEvent("generating_back_abandon", {
        job_id: priorJobId || "",
        to: backToWizard ? "wizard" : "import",
      })
    })
  }, [history])

  const analysisFailed = status === "failed" || status === "prep_failed"

  /** Phase 1 progress (0–50%) — unsigned only, before signup gate. */
  useEffect(() => {
    if (sessionExpired || claimBlocked || analysisFailed) return
    if (!isLoaded || isSignedIn) return
    if (unsignedPhase === "signup") return

    const id = window.setInterval(() => {
      setPreSignupStepIndex((i) => {
        if (i >= GENERATING_STEPS.length - 1) return i
        return i + 1
      })
    }, GENERATING_STEP_MS)
    return () => window.clearInterval(id)
  }, [
    analysisFailed,
    claimBlocked,
    isLoaded,
    isSignedIn,
    sessionExpired,
    unsignedPhase,
  ])

  /** Phase 2 progress (50–100%) — signed in until results. */
  useEffect(() => {
    if (sessionExpired || claimBlocked || analysisFailed) return
    if (!isLoaded || !isSignedIn) return

    const id = window.setInterval(() => {
      setPostSignupStepIndex((i) => {
        if (i >= GENERATING_STEPS.length - 1) return i
        return i + 1
      })
    }, GENERATING_STEP_MS)
    return () => window.clearInterval(id)
  }, [analysisFailed, claimBlocked, isLoaded, isSignedIn, sessionExpired])

  /** After the checklist finishes, reveal signup (unsigned only). */
  useEffect(() => {
    if (!isLoaded || isSignedIn) return
    if (sessionExpired || claimBlocked || analysisFailed) return
    if (unsignedPhase !== "loading") return
    if (preSignupStepIndex < GENERATING_STEPS.length - 1) return

    const id = window.setTimeout(() => {
      setUnsignedPhase("signup")
      trackDemoEntryAnalysisEvent("signup_gate_shown", { job_id: jobId || "" })
    }, GENERATING_COMPLETE_HOLD_MS)
    return () => window.clearTimeout(id)
  }, [
    analysisFailed,
    claimBlocked,
    preSignupStepIndex,
    isLoaded,
    isSignedIn,
    jobId,
    sessionExpired,
    unsignedPhase,
  ])

  const markSessionExpired = useCallback((message?: string) => {
    pollStoppedRef.current = true
    setSessionExpired(true)
    setStatus(null)
    setError(message || DEMO_SESSION_EXPIRED_COPY)
    writeDemoSession({
      jobId: undefined,
      handoffCode: undefined,
      pollToken: undefined,
      claimed: false,
      claimAttempted: false,
      readyPayload: null,
      step: "import",
    })
    trackDemoEntryAnalysisEvent("poll_unauthorized", { job_id: jobId || "" })
  }, [jobId])

  const markClaimBlocked = useCallback(
    (message: string, kind: "rate_limit" | "tes_daily" | "other" = "other", job?: string) => {
      claimBlockedRef.current = true
      pollStoppedRef.current = true
      setClaimBlockKind(kind)
      setClaimBlocked(true)
      setError(message)
      trackDemoEntryAnalysisEvent("claim_blocked", {
        job_id: job || jobId || "",
        kind,
      })
    },
    [jobId]
  )

  const tryRecoverPollCookie = useCallback(async (): Promise<boolean> => {
    if (!jobId) return false
    if (redeemAttemptedRef.current) return false
    if (!canAttemptHandoffRedeem()) return false
    redeemAttemptedRef.current = true
    const result = await redeemDemoHandoffOnce(jobId)
    trackDemoEntryAnalysisEvent("handoff_redeem", { job_id: jobId, result })
    return result === "ok" || result === "already_spent"
  }, [jobId])

  const goResults = useCallback(() => {
    clearPendingAuthRedirect()
    writeDemoSession({ step: "results" })
    history.replace(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/results`)
  }, [history])

  const goStartOver = useCallback(() => {
    void restartDemoEntryAnalysis((path) => history.push(path))
  }, [history])

  /** Keep cohort + instruments; drop failed job so wizard can prep again. */
  const goBackToInstrument = useCallback(() => {
    writeDemoSession({
      step: "wizard",
      jobId: undefined,
      handoffCode: undefined,
      pollToken: undefined,
      claimed: false,
      claimAttempted: false,
      readyPayload: null,
    })
    trackDemoEntryAnalysisEvent("back_to_instrument", { from: "generating_failed" })
    history.push(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/wizard`)
  }, [history])

  /**
   * CLAIM GATE: only after Clerk reports a session (post signup/sign-in, or already logged in).
   * Never from the unsigned signup screen. Never from the poll loop.
   */
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !jobId) return
    if (sessionExpired || claimBlocked) return
    // Arrived from auth — drop the durable return marker so later /dashboard visits are normal.
    clearPendingAuthRedirect()
    if (shouldSkipDemoClaim(jobId) || claimedRef.current) {
      claimedRef.current = true
      return
    }
    if (claimKickoffRef.current) return
    claimKickoffRef.current = true

    let cancelled = false

    const run = async () => {
      setError(null)
      try {
        // Prove / restore poll cookie before claim (JWT alone is not enough).
        try {
          const peek = await pollDemoJob(jobId)
          if (cancelled) return
          setStatus(peek.status)
          if (peek.status === "tes_running" || peek.status === "ready") {
            // Already claimed earlier — do not POST /claim again.
            claimedRef.current = true
            writeDemoSession({ claimed: true, claimAttempted: true })
            if (peek.status === "ready" && (peek.payload || peek.result)) {
              writeDemoSession({
                readyPayload: peek.payload || peek.result || null,
                step: "results",
              })
              goResults()
            }
            return
          }
          if (peek.status === "prep_failed") {
            pollStoppedRef.current = true
            setError(
              peek.error_message ||
                "Unfortunately, we couldn’t locate this instrument in our data sources. Please sync a CSV with other instruments."
            )
            return
          }
        } catch (err) {
          if (cancelled) return
          if (isDemoPollUnauthorized(err)) {
            const recovered = await tryRecoverPollCookie()
            if (!recovered) {
              markSessionExpired()
              return
            }
            try {
              await pollDemoJob(jobId)
            } catch (retryErr) {
              if (isDemoPollUnauthorized(retryErr)) {
                markSessionExpired()
                return
              }
              // Non-auth peek failure — still attempt claim; BE will decide.
            }
          } else if (isDemoRateLimited(err)) {
            markClaimBlocked(demoErrorUserMessage(err), "rate_limit", jobId)
            return
          }
        }

        if (cancelled || claimBlockedRef.current || pollStoppedRef.current) return

        const res = await claimDemoJobOnce(jobId)
        if (cancelled) return
        claimedRef.current = true
        setStatus(res.status)
        trackDemoEntryAnalysisEvent("claim", { job_id: jobId, status: res.status })
        if (res.status === "ready" && (res.payload || res.result)) {
          writeDemoSession({
            readyPayload: res.payload || res.result || null,
            step: "results",
          })
          trackDemoEntryAnalysisEvent("results_viewed", { job_id: jobId })
          goResults()
        }
      } catch (err) {
        if (cancelled) return
        if (isDemoPollUnauthorized(err)) {
          const recovered = await tryRecoverPollCookie()
          if (recovered) {
            allowDemoClaimRetryAfterCookieFix(jobId)
            try {
              const res = await claimDemoJobOnce(jobId)
              if (cancelled) return
              claimedRef.current = true
              setStatus(res.status)
              trackDemoEntryAnalysisEvent("claim", { job_id: jobId, status: res.status })
              if (res.status === "ready" && (res.payload || res.result)) {
                writeDemoSession({
                  readyPayload: res.payload || res.result || null,
                  step: "results",
                })
                goResults()
              }
              return
            } catch (retryErr) {
              if (isDemoPollUnauthorized(retryErr)) {
                markSessionExpired()
                return
              }
              if (isDemoTesDailyUserLimit(retryErr)) {
                markClaimBlocked(demoErrorUserMessage(retryErr), "tes_daily", jobId)
                return
              }
              markClaimBlocked(
                demoErrorUserMessage(retryErr),
                isDemoRateLimited(retryErr) ? "rate_limit" : "other",
                jobId
              )
              return
            }
          }
          markSessionExpired()
          return
        }
        if (isDemoTesDailyUserLimit(err)) {
          claimedRef.current = true
          markClaimBlocked(demoErrorUserMessage(err), "tes_daily", jobId)
          trackDemoEntryAnalysisEvent("claim_fail", { job_id: jobId, reason: "tes_daily_user" })
          return
        }
        if (isDemoRateLimited(err)) {
          markClaimBlocked(demoErrorUserMessage(err), "rate_limit", jobId)
          trackDemoEntryAnalysisEvent("claim_fail", { job_id: jobId, reason: "rate_limit" })
          return
        }
        if (err instanceof Error && err.message.includes("already")) {
          claimedRef.current = true
          return
        }
        markClaimBlocked(demoErrorUserMessage(err), "other", jobId)
        trackDemoEntryAnalysisEvent("claim_fail", { job_id: jobId })
        claimKickoffRef.current = false
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [
    claimBlocked,
    goResults,
    isLoaded,
    isSignedIn,
    jobId,
    markClaimBlocked,
    markSessionExpired,
    sessionExpired,
    tryRecoverPollCookie,
  ])

  /**
   * POLL only — never POST /claim.
   * Unsigned: detect prep_failed while showing signup.
   * Signed in: watch TES after claim.
   */
  useEffect(() => {
    if (!jobId || sessionExpired || claimBlocked) return
    if (!isLoaded) return

    let cancelled = false
    let timer: number | undefined

    const schedule = (ms: number, fn: () => void) => {
      timer = window.setTimeout(fn, ms)
    }

    const tick = async () => {
      if (cancelled || pollStoppedRef.current || claimBlockedRef.current) return
      try {
        const res = await pollDemoJob(jobId)
        if (cancelled || pollStoppedRef.current || claimBlockedRef.current) return
        setStatus(res.status)
        if (!claimBlockedRef.current) setError(null)

        if (res.status === "prep_failed") {
          pollStoppedRef.current = true
          setError(
            res.error_message ||
              "Unfortunately, we couldn’t locate this instrument in our data sources. Please sync a CSV with other instruments."
          )
          trackDemoEntryAnalysisEvent("prep_fail", { job_id: jobId, cta: res.cta || "" })
          return
        }

        if (res.status === "ready" && (res.payload || res.result)) {
          writeDemoSession({
            readyPayload: res.payload || res.result || null,
            step: "results",
            claimed: true,
            claimAttempted: true,
            handoffCode: undefined,
          })
          claimedRef.current = true
          trackDemoEntryAnalysisEvent("tes_ok", { job_id: jobId })
          goResults()
          return
        }

        if (res.status === "failed") {
          pollStoppedRef.current = true
          setError(res.error_message || "Analysis failed. You can retry once.")
          trackDemoEntryAnalysisEvent("tes_fail", { job_id: jobId })
          return
        }

        if (res.status === "tes_running") {
          claimedRef.current = true
          writeDemoSession({ claimed: true, claimAttempted: true })
        }

        // Unsigned: keep polling through the loading checklist; stop once signup owns the screen.
        if (!isSignedIn) {
          if (unsignedPhaseRef.current === "signup") return
          schedule(2500, () => void tick())
          return
        }

        schedule(2500, () => void tick())
      } catch (err) {
        if (cancelled || pollStoppedRef.current || claimBlockedRef.current) return

        if (isDemoPollUnauthorized(err)) {
          // Unsigned: keep handoff for post-signup claim — do not redeem here.
          if (!isSignedIn) return

          const recovered = await tryRecoverPollCookie()
          if (recovered && !cancelled && !pollStoppedRef.current) {
            schedule(300, () => void tick())
            return
          }
          markSessionExpired()
          return
        }

        if (isDemoRateLimited(err)) {
          markClaimBlocked(demoErrorUserMessage(err), "rate_limit", jobId)
          return
        }

        setError(demoErrorUserMessage(err))
        if (!isSignedIn) {
          if (unsignedPhaseRef.current === "signup") return
          schedule(5000, () => void tick())
          return
        }
        schedule(5000, () => void tick())
      }
    }

    void tick()

    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [
    claimBlocked,
    goResults,
    isLoaded,
    isSignedIn,
    jobId,
    markClaimBlocked,
    markSessionExpired,
    sessionExpired,
    tryRecoverPollCookie,
  ])

  const onRetryTes = async () => {
    if (!jobId || sessionExpired || !isSignedIn) return
    setRetrying(true)
    setError(null)
    pollStoppedRef.current = false
    try {
      const res = await retryDemoTes(jobId)
      setStatus(res.status)
      trackDemoEntryAnalysisEvent("retry_tes", { job_id: jobId })
    } catch (err) {
      if (isDemoPollUnauthorized(err)) {
        const recovered = await tryRecoverPollCookie()
        if (recovered) {
          try {
            const res = await retryDemoTes(jobId)
            setStatus(res.status)
            return
          } catch (retryErr) {
            if (isDemoPollUnauthorized(retryErr)) {
              markSessionExpired()
              return
            }
            setError(demoErrorUserMessage(retryErr))
            return
          }
        }
        markSessionExpired()
        return
      }
      setError(demoErrorUserMessage(err))
    } finally {
      setRetrying(false)
    }
  }

  if (sessionExpired) {
    return (
      <DemoEntryAnalysisShell layout="compact">
        <DemoCenteredMessage
          title="Demo session expired"
          description={error || DEMO_SESSION_EXPIRED_COPY}
          action={
            <Button type="button" variant="gradient" onClick={goStartOver}>
              Generate another report
            </Button>
          }
        />
      </DemoEntryAnalysisShell>
    )
  }

  if (claimBlocked) {
    const isDaily = claimBlockKind === "tes_daily"
    return (
      <DemoEntryAnalysisShell activeStepIndex={3}>
        <DemoCenteredMessage
          title={isDaily ? "You’ve used today’s free demo" : "Too many requests"}
          description={
            error ||
            (isDaily
              ? DEMO_TES_DAILY_LIMIT_COPY
              : "We couldn’t start the final analysis for this preview right now. Please try again in a few minutes.")
          }
          action={
            <div className="flex flex-col items-center gap-3">
              <Button type="button" variant="gradient" onClick={goStartOver}>
                Start over
              </Button>
            </div>
          }
        />
      </DemoEntryAnalysisShell>
    )
  }

  if (!jobId) {
    return (
      <DemoEntryAnalysisShell activeStepIndex={3}>
        <DemoLoadingBlock centered />
      </DemoEntryAnalysisShell>
    )
  }

  if (analysisFailed) {
    return (
      <DemoEntryAnalysisShell activeStepIndex={3}>
        <DemoCenteredMessage
          title="Something went wrong"
          description={
            error ||
            "Change the instrument or quote variant, or sync another file."
          }
          action={
            <div className="flex w-full flex-col items-center gap-4">
              <div className="flex flex-wrap justify-center gap-2">
                <Button type="button" variant="gradient" onClick={goBackToInstrument}>
                  Back to instrument
                </Button>
                {status === "failed" && isSignedIn ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={retrying}
                    onClick={() => void onRetryTes()}
                  >
                    {retrying ? "Retrying…" : "Retry analysis"}
                  </Button>
                ) : null}
                <Button type="button" variant="outline" onClick={goStartOver}>
                  Sync another CSV
                </Button>
              </div>
              <button
                type="button"
                className={cn(AUTH_FONT, AUTH_TROPICAL, "text-xs hover:opacity-90")}
                onClick={goStartOver}
              >
                Start over
              </button>
            </div>
          }
        />
      </DemoEntryAnalysisShell>
    )
  }

  // Not signed in: loading checklist crossfades into signup gate (never /claim until Clerk session).
  if (isLoaded && !isSignedIn) {
    return (
      <DemoEntryAnalysisShell>
        <DemoUnsignedSignupGate
          unsignedPhase={unsignedPhase}
          preSignupStepIndex={preSignupStepIndex}
          footerNote={error}
        />
      </DemoEntryAnalysisShell>
    )
  }

  if (!isLoaded) {
    return (
      <DemoEntryAnalysisShell activeStepIndex={3}>
        <DemoLoadingBlock centered />
      </DemoEntryAnalysisShell>
    )
  }

  // analysisFailed (prep_failed / failed) returns above — creep on last step until ready, then 100%.
  const postSignupHold = status !== "ready"

  return (
    <DemoEntryAnalysisShell>
      <DemoGeneratingProgress
        key="post-signup-generating"
        activeStepIndex={postSignupStepIndex}
        progressPhase="post_signup"
        holdBeforeComplete={postSignupHold}
        footerNote={error}
      />
    </DemoEntryAnalysisShell>
  )
}
